import React, { useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout/legacy";
import { Global } from "../helpers/Global";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

export const PanelReportesSIL = () => {
  // =========================
  // Validez reportes
  // =========================
  const TTL_BASE_HORAS = 32;
  const TTL_VIERNES_HORAS = 80;
  const TTL_SABADO_HORAS = 66;
  const TTL_DOMINGO_HORAS = 48;
  const HOUR_MS = 60 * 60 * 1000;

  // Layout fijo
  const W_FIJO = 1;
  const H_FIJO = 2;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [configSIL, setConfigSIL] = useState({
    cols_canvas: 120,
    filas_canvas: 40,
    col_px: 50,
    row_height: 60,
  });

  const [nodos, setNodos] = useState([]);
  const [ultimosReportes, setUltimosReportes] = useState([]);

  const tableroRef = useRef(null);
  const scrollTopRef = useRef(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // =========================
  // Dimensiones lienzo
  // =========================
  const COLS = Math.max(1, Number(configSIL.cols_canvas || 120));
  const FILAS = Math.max(1, Number(configSIL.filas_canvas || 40));
  const COL_PX = Math.max(20, Number(configSIL.col_px || 50));
  const ROW_HEIGHT = Math.max(20, Number(configSIL.row_height || 60));

  const GRID_WIDTH = COLS * COL_PX;
  const GRID_HEIGHT = FILAS * ROW_HEIGHT;

  // =========================
  // Drag-to-pan (click izquierdo)
  // =========================
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
    moved: false,
  });

  const onPanMouseDown = (e) => {
    // Solo click izquierdo
    if (e.button !== 0) return;

    // Si empiezas sobre controles, no panear
    const tag = (e.target?.tagName || "").toLowerCase();
    if (["input", "textarea", "select", "button", "a"].includes(tag)) return;

    const el = tableroRef.current;
    if (!el) return;

    panRef.current.active = true;
    panRef.current.moved = false;
    panRef.current.startX = e.clientX;
    panRef.current.startY = e.clientY;
    panRef.current.startScrollLeft = el.scrollLeft;
    panRef.current.startScrollTop = el.scrollTop;

    setIsPanning(true);
  };

  const onPanMouseMove = (e) => {
    if (!panRef.current.active) return;
    const el = tableroRef.current;
    if (!el) return;

    const dx = e.clientX - panRef.current.startX;
    const dy = e.clientY - panRef.current.startY;

    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) panRef.current.moved = true;

    el.scrollLeft = panRef.current.startScrollLeft - dx;
    el.scrollTop = panRef.current.startScrollTop - dy;
  };

  const stopPan = () => {
    if (!panRef.current.active) return;
    panRef.current.active = false;
    setIsPanning(false);
  };

  // =========================
  // Fetch
  // =========================
  const fetchConfigSIL = async () => {
    const res = await fetch(`${Global.url}nodos/config`, { headers: authHeaders() });
    const data = await res.json();
    setConfigSIL(data);
  };

  const fetchOrganigrama = async () => {
    const res = await fetch(`${Global.url}nodos/organigrama`, { headers: authHeaders() });
    const data = await res.json();
    setNodos(
      (data || []).map((n) => ({
        ...n,
        id: Number(n.id),
        x: n.x ?? null,
        y: n.y ?? null,
        w: W_FIJO,
        h: H_FIJO,
        enPanel: n.x !== null && n.y !== null,
      }))
    );
  };

  const fetchUltimosReportes = async () => {
    const res = await fetch(`${Global.url}reportes/ultimo`, { headers: authHeaders() });
    const data = await res.json();
    setUltimosReportes(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchConfigSIL(), fetchOrganigrama(), fetchUltimosReportes()]);
      } catch (e) {
        console.error(e);
        setError("❌ No se pudieron cargar los datos del panel.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      fetchUltimosReportes().catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // =========================
  // Helpers
  // =========================
  const reportesMap = useMemo(() => {
    const m = new Map();
    ultimosReportes.forEach((r) => m.set(Number(r.nodo_id), r));
    return m;
  }, [ultimosReportes]);

  const nodosPanel = useMemo(() => nodos.filter((n) => n.enPanel), [nodos]);

  const layoutPanel = useMemo(
    () =>
      nodosPanel.map((n) => ({
        i: String(n.id),
        x: n.x ?? 0,
        y: n.y ?? 0,
        w: W_FIJO,
        h: H_FIJO,
        static: true,
      })),
    [nodosPanel]
  );

  const ttlHorasSegunDia = (ms) => {
    const d = new Date(ms).getDay(); // 0 dom .. 5 vie .. 6 sáb
    if (d === 5) return TTL_VIERNES_HORAS;
    if (d === 6) return TTL_SABADO_HORAS;
    if (d === 0) return TTL_DOMINGO_HORAS;
    return TTL_BASE_HORAS;
  };

  const getEstadoVigente = (rep) => {
    if (!rep?.creado_en) return { vigente: false, estado: null, expiraMs: null };

    const creadoMs = new Date(rep.creado_en).getTime();
    if (!Number.isFinite(creadoMs)) return { vigente: false, estado: null, expiraMs: null };

    const expiraMs = creadoMs + ttlHorasSegunDia(creadoMs) * HOUR_MS;
    const vigente = Date.now() <= expiraMs;

    return { vigente, estado: rep.estado, expiraMs };
  };

  const formatearFecha = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  const colorCard = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (v === "verde") return "#dff6e4";
    if (v === "amarillo") return "#fff4cc";
    if (v === "rojo") return "#ffe0e0";
    return "#eef2f7";
  };

  const bordeCard = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (v === "verde") return "1px solid rgba(39,174,96,.35)";
    if (v === "amarillo") return "1px solid rgba(241,196,15,.45)";
    if (v === "rojo") return "1px solid rgba(192,57,43,.35)";
    return "1px solid rgba(0,0,0,.10)";
  };

  // =========================
  // Scroll sync (barra superior)
  // =========================
  const syncScrollTop = (e) => {
    if (tableroRef.current) tableroRef.current.scrollLeft = e.target.scrollLeft;
  };

  const syncScrollMain = (e) => {
    if (scrollTopRef.current) scrollTopRef.current.scrollLeft = e.target.scrollLeft;
  };

  if (loading) return <div className="sil-panel-loading">Cargando panel…</div>;

  return (
    <div className="sil-panel">
      <h2 className="sil-panel-title">SIL · Panel de Reportes</h2>

      {error && <div className="sil-panel-error">{error}</div>}

      {/* Scroll horizontal superior */}
      <div
        ref={scrollTopRef}
        onScroll={syncScrollTop}
        className="sil-panel-scroll-top"
      >
        <div style={{ width: GRID_WIDTH }} />
      </div>

      {/* Canvas (pan con click izquierdo) */}
      <div
        ref={tableroRef}
        onScroll={syncScrollMain}
        className={`sil-panel-canvas ${isPanning ? "is-panning" : ""}`}
        onMouseDown={onPanMouseDown}
        onMouseMove={onPanMouseMove}
        onMouseUp={stopPan}
        onMouseLeave={stopPan}
      >
        <div style={{ width: GRID_WIDTH, height: GRID_HEIGHT }}>
          <GridLayout
            layout={layoutPanel}
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            width={GRID_WIDTH}
            maxRows={FILAS}
            margin={[12, 12]}
            containerPadding={[12, 12]}
            isResizable={false}
            isDraggable={false}
            preventCollision
            compactType={null}
          >
            {nodosPanel.map((n) => {
              const rep = reportesMap.get(n.id);
              const { vigente, estado, expiraMs } = getEstadoVigente(rep);

              const bg = vigente ? colorCard(estado) : "#ffffff";
              const border = vigente ? bordeCard(estado) : "1px dashed rgba(0,0,0,.20)";

              const usuarioAsignado = n?.usuario_id
                ? `${n.nombre || ""} ${n.apellidos || ""}`.trim()
                : "Sin asignar";

              const tooltip = rep
                ? `Último: ${formatearFecha(rep.creado_en)}\nVálido hasta: ${expiraMs ? new Date(expiraMs).toLocaleString() : "—"}\nAutor: ${(rep.nombre || "")} ${(rep.apellidos || "")}`.trim() +
                  (rep.comentario ? `\n\n${rep.comentario}` : "")
                : "Sin reportes";

              return (
                <div key={String(n.id)} style={{ cursor: "default" }}>
                  <div
                    className="sil-nodo-card-inner"
                    title={tooltip}
                    onClick={(e) => {
                      // Evitar clicks accidentales tras pan
                      if (panRef.current.moved) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    style={{
                      height: "100%",
                      background: bg,
                      border,
                      borderRadius: 10,
                      padding: 10,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      gap: 6,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800, lineHeight: 1.15 }}>
                        {n.titulo}
                      </div>
                      <div className="muted" style={{ fontSize: 13 }}>
                        {usuarioAsignado}{n.etiqueta ? ` · ${n.etiqueta}` : ""}
                      </div>
                    </div>

                    {/* ✅ Solo fecha. Quitamos texto del estado */}
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {rep?.creado_en ? formatearFecha(rep.creado_en) : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </GridLayout>
        </div>
      </div>
    </div>
  );
};
